import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IntlShape, useIntl } from 'react-intl';
import { reset } from '../../../actions/signup-actions';
import { useAppDispatch, useAppSelector } from '../../../stores/redux.store';
import { SignupErrorCode } from '../../../types';
import { useRegisterComponent } from '../../orchestrator';
import { useNavigateSoftOrHard } from '../../../utilities/hooks/useNavigateSoftOrHard';

export type SignupErrorInfo = {
  errorCode: SignupErrorCode;
  errorMessage: string;
};

export type UserJourneyContextError = {
  errorCode: 'account_does_not_exist';
  errorMessage: string;
};

type UseSignupOptions = {
  forceRedirect?: boolean;
};

export const useSignup = ({ forceRedirect = true }: UseSignupOptions = {}) => {
  useRegisterComponent(useSignup.name);
  const dispatch = useAppDispatch();
  const errorCode = useAppSelector(state => state.signup?.error);
  const redirectTo = useAppSelector(state => state.signup?.redirectTo);

  const navigate = useNavigateSoftOrHard();

  useEffect(() => {
    if (redirectTo && forceRedirect) {
      try {
        const url = new URL(redirectTo);
        navigate(url.toString());
      } catch (e) {
        // invalid URL provided
      }
    }
  }, [redirectTo, forceRedirect, navigate]);

  useEffect(() => {
    // On unmount, dispatch reset:
    return () => {
      dispatch(reset());
    };
  }, [dispatch]);

  const { formatMessage } = useIntl();

  const error: SignupErrorInfo | undefined = useMemo(() => {
    return errorCode
      ? {
          errorCode,
          errorMessage: signupErrorMessageFromCode(errorCode, formatMessage),
        }
      : undefined;
  }, [errorCode, formatMessage]);

  const [queryParams] = useSearchParams();
  const isRedirectFromResetPassord = queryParams.get('errorCode') === 'account_does_not_exist';

  const userJourneyContextError: UserJourneyContextError | undefined = isRedirectFromResetPassord
    ? {
        errorCode: 'account_does_not_exist' as const,
        errorMessage: formatMessage({
          id: 'signup.form.redirect.account.does.not.exist.error',
          defaultMessage: "We can't find an account for that email address.",
          description:
            'Error message shown when user tries to reset password for account that does not exist and is redirected to sign up',
        }),
      }
    : undefined;

  return {
    /**
     * An error resulting from the user's actions on the signup page (often server error from
     * submitting the form).
     * */
    error,
    /** URL to redirect to, if opted out of forced redirect */
    redirectTo,
    /**
     * An error within the wider context of the user's journey, during which they have been
     * redirected to or otherwise landed on the signup page (i.e. not a result of something they did
     * while on the page).
     * */
    userJourneyContextError,
  };
};

const signupErrorMessageFromCode = (
  errorCode: SignupErrorCode,
  formatMessage: IntlShape['formatMessage']
) => {
  switch (errorCode) {
    case 'unknown':
      return formatMessage({
        id: 'signup.error.unknown',
        defaultMessage: 'Something went wrong while attempting to sign up. Please try again.',
      });
    case 'captcha':
      return formatMessage({
        id: 'signup.error.captcha',
        defaultMessage:
          'We are having trouble verifying reCAPTCHA for this request. Please try again. If the problem persists, try another browser/device or reach out to Atlassian Support.',
      });
    case 'captchaScoreTooLow':
      return formatMessage({
        id: 'signup.error.captcha.score_too_low',
        defaultMessage:
          "We'd like to make sure it's really you creating your account. Please confirm you're not a robot.",
        description: 'Message asking user to tick the reCAPTCHA checkbox and resubmit the form',
      });
    case 'cookieMissing':
      return formatMessage({
        id: 'signup.error.cookie.missing',
        defaultMessage:
          'We’re unable to create your account at this time. Please make sure strictly necessary cookies are enabled.',
      });
    case 'request_error':
      return formatMessage({
        id: 'signup.error.request_error',
        defaultMessage:
          'Unable to connect. Check your internet connection or the network settings for your browser and try again.',
      });
    case 'unexpectedError':
      return formatMessage({
        id: 'signup.error.unexpected_error',
        defaultMessage: "We couldn't create your account. Try again in a few minutes.",
      });
    default:
      return formatMessage(
        {
          id: 'signup.form.random.error',
          defaultMessage:
            'An error occurred while attempting to sign up: {error}. Please try again or reach out to Atlassian Support.',
        },
        {
          error: errorCode,
        }
      );
  }
};
